import { Dialog } from './FarChat/ChatFeedbackOverlays';
import React, { useState, useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { toUserFacingError, USER_FACING_ERROR_MESSAGES } from '../utils/userFacingError';
import CacLogin from './CacLogin';
import SignupForm from './SignupForm';

const API_BASE_URL =
  process.env.REACT_APP_API_URL || '';

interface EmailGateProps {
  open: boolean;
  onAuthSuccess: (email: string) => void;
}

type AuthStep =
  | 'email'
  | 'code'
  | 'totp-code'
  | 'qr-enroll'
  | 'backup-codes'
  | 'backup-login'
  | 'reset-email-code'
  | 'reset-qr'
  | 'reset-backup-codes';

const EmailGate: React.FC<EmailGateProps> = ({ open, onAuthSuccess }) => {
  const [step, setStep] = useState<AuthStep>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [qrPng, setQrPng] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [backupCodesReason, setBackupCodesReason] = useState<'first_time' | 'consumed_all'>('first_time');
  const [backupCode, setBackupCode] = useState('');
  const [showBackupInput, setShowBackupInput] = useState(false);
  // Tracks whether the current user has any backup codes on file.
  // Populated from /api/auth/send-code so the "Use backup code" toggle on
  // the code-entry screen is hidden for first-time users (no codes yet).
  // Re-evaluated on every /send-code call so it stays fresh if the user
  // navigates back to the email step and submits a different address.
  const [hasBackupCodes, setHasBackupCodes] = useState(false);
  const [enrollAfterLogin, setEnrollAfterLogin] = useState(false);
  // Controls the in-app self-signup dialog.
  const [signupOpen, setSignupOpen] = useState(false);
  const [cacAvailable, setCacAvailable] = useState(false);

  useEffect(() => {
    if (!open) {
      setEnrollAfterLogin(false);
    }
  }, [open]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const interval = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const clearErrors = () => setError('');

  // Centralized 429 handler. Sets a canonical rate-limit error message via the
  // single `error` state; backend detail is discarded. Callers pass the
  // appropriate canonical message: `login_rate_limit` for verify-attempt
  // lockouts (default) and `otp_too_many_requests` for too-many-code-REQUESTS
  // (send-code 429) so the copy tells the user to enter the newest emailed code
  // rather than request another.
  const applyRateLimitResponse = async (
    resp: Response,
    message: string = USER_FACING_ERROR_MESSAGES.login_rate_limit,
  ): Promise<string> => {
    // Consume (and discard) the response body so the connection is released.
    await resp.json().catch(() => {});
    setError(message);
    return message;
  };

  const validateEmail = (e: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  };

  const maskEmail = (e: string): string => {
    const atIndex = e.indexOf('@');
    if (atIndex <= 0) return e;
    const local = e.slice(0, atIndex);
    const domain = e.slice(atIndex);
    return `${local.charAt(0)}***${domain}`;
  };

  const handleEmailSubmit = async (
    options?: { intent?: 'login' | 'totp_enroll' },
  ) => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError(USER_FACING_ERROR_MESSAGES.missing_email);
      return;
    }
    if (!validateEmail(trimmed)) {
      setError(USER_FACING_ERROR_MESSAGES.invalid_email);
      return;
    }

    // Intent: pass through if explicitly provided (e.g. enrollment path).
    // Otherwise read the current enrollAfterLogin flag — but the flag is
    // primarily set by handleUseTotp and the explicit option avoids state
    // races between setState and the subsequent fetch.
    const intent: 'login' | 'totp_enroll' =
      options?.intent ?? (enrollAfterLogin ? 'totp_enroll' : 'login');
    const requestBody: Record<string, unknown> = { email: trimmed };
    if (intent === 'totp_enroll') {
      requestBody.intent = 'totp_enroll';
    }

    clearErrors();
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/send-code`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (resp.status === 403) {
        setError(USER_FACING_ERROR_MESSAGES.not_authorized);
        return;
      }
      if (resp.status === 429) {
        await applyRateLimitResponse(
          resp,
          USER_FACING_ERROR_MESSAGES.otp_too_many_requests,
        );
        return;
      }
      if (resp.status === 501 || resp.status === 503) {
        setError(USER_FACING_ERROR_MESSAGES.email_service_unavailable);
        return;
      }

      const data = await resp.json();

      if (data.status === 'authenticated') {
        // Bypass mode (AUTH_SKIP_CODE on server): backend issued session cookie directly
        onAuthSuccess(trimmed);
        return;
      }

      if (data.status === 'code_sent') {
        setHasBackupCodes(Boolean(data.has_backup_codes));
        setStep('code');
        setResendCooldown(60);
      } else {
        setError(USER_FACING_ERROR_MESSAGES.unexpected_response);
      }
    } catch {
      setError(USER_FACING_ERROR_MESSAGES.login_network_error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!code || code.length !== 6) {
      setError(USER_FACING_ERROR_MESSAGES.missing_code);
      return;
    }

    clearErrors();
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/verify-code`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed, code }),
      });

      if (resp.status === 429) {
        await applyRateLimitResponse(resp);
        return;
      }
      if (resp.status === 400 || resp.status === 401) {
        setError(USER_FACING_ERROR_MESSAGES.invalid_code);
        return;
      }

      const data = await resp.json();
      if (enrollAfterLogin) {
        // User came in via "Use authenticator app instead" with no enrollment.
        // Email-OTP just succeeded; immediately fetch the QR and advance to
        // the QR-scan step. There is no intermediate confirmation screen —
        // this mirrors the reset flow's shape (email → code → QR → success).
        setCode('');
        clearErrors();
        await handleStartEnroll();
        return;
      }
      // First-login backup-code provisioning: backend returns fresh codes
      // when the user's stored array is empty. Show the save-codes screen
      // before completing auth (matches the TOTP-verify flow).
      if (data && data.backup_codes && data.backup_codes.length > 0) {
        setBackupCodesReason('first_time');
        setBackupCodes(data.backup_codes);
        setStep('backup-codes');
        return;
      }
      onAuthSuccess(trimmed);
    } catch {
      setError(USER_FACING_ERROR_MESSAGES.login_network_error);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    const trimmed = email.trim().toLowerCase();
    clearErrors();
    // Clear any code the user already typed: on a resend the server overwrites
    // the stored OTP, so only the freshest emailed code is valid. Clearing here
    // covers every outcome (200 success, 429, network error) so a stale code
    // can never linger and trigger a repeat 401 on verify.
    setCode('');
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/send-code`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });

      if (resp.status === 429) {
        await applyRateLimitResponse(
          resp,
          USER_FACING_ERROR_MESSAGES.otp_too_many_requests,
        );
        return;
      }
      if (resp.status === 501 || resp.status === 503) {
        setError(USER_FACING_ERROR_MESSAGES.email_service_unavailable);
        return;
      }

      const data = await resp.json();

      if (data.status === 'authenticated') {
        // Bypass mode — same as handleEmailSubmit.
        onAuthSuccess(trimmed);
        return;
      }

      if (data.status === 'code_sent') {
        setHasBackupCodes(Boolean(data.has_backup_codes));
        setResendCooldown(60);
      } else {
        setError(USER_FACING_ERROR_MESSAGES.unexpected_response);
      }
    } catch {
      setError(USER_FACING_ERROR_MESSAGES.login_network_error);
    } finally {
      setLoading(false);
    }
  };

  const handleUseTotp = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError(USER_FACING_ERROR_MESSAGES.missing_email);
      return;
    }
    if (!validateEmail(trimmed)) {
      setError(USER_FACING_ERROR_MESSAGES.invalid_email);
      return;
    }

    clearErrors();
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/totp/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });

      if (resp.status === 403) {
        setError(USER_FACING_ERROR_MESSAGES.not_authorized);
        return;
      }
      if (resp.status === 429) {
        await applyRateLimitResponse(resp);
        return;
      }

      const data = await resp.json().catch(() => ({}));

      if (data.status === 'enrollment_required') {
        // Backend declined to mint a TOTP secret without first proving inbox
        // control. Route the user through the email-OTP path; after they
        // verify, we'll prompt them to enroll. (Branches on body.status —
        // /totp/login returns HTTP 200 for this expected flow.)
        setEnrollAfterLogin(true);
        setCode('');
        setShowBackupInput(false);
        setBackupCode('');
        setQrPng(null);
        // Trigger /auth/send-code (preserves existing rate-limit / 429 / 503
        // handling). Pass intent='totp_enroll' explicitly so the backend
        // sends the authenticator-setup email template instead of the
        // routine login email — setState for enrollAfterLogin above hasn't
        // flushed yet, so the option arg is the safe channel here.
        await handleEmailSubmit({ intent: 'totp_enroll' });
        return;
      }

      if (data.status === 'totp_required') {
        setCode('');
        setShowBackupInput(false);
        setBackupCode('');
        setStep('totp-code');
      } else {
        setError(USER_FACING_ERROR_MESSAGES.unexpected_response);
      }
    } catch {
      setError(USER_FACING_ERROR_MESSAGES.login_network_error);
    } finally {
      setLoading(false);
    }
  };

  const handleTotpVerify = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!code || code.length !== 6) {
      setError(USER_FACING_ERROR_MESSAGES.missing_code);
      return;
    }

    clearErrors();
    setLoading(true);
    try {
      // During enrollment (step 'qr-enroll') the server uses the session
      // cookie's email — we MUST NOT send `email` in the body, otherwise an
      // attacker with a leaked session could finalize TOTP for a different
      // address. See spec §4.3.
      const verifyBody =
        step === 'qr-enroll' ? { code } : { email: trimmed, code };
      const resp = await fetch(`${API_BASE_URL}/api/auth/totp/verify`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(verifyBody),
      });

      if (resp.status === 429) {
        await applyRateLimitResponse(resp);
        return;
      }
      if (resp.status === 400 || resp.status === 401) {
        setError(USER_FACING_ERROR_MESSAGES.invalid_code);
        return;
      }
      if (resp.status === 403) {
        setError(USER_FACING_ERROR_MESSAGES.not_authorized);
        return;
      }

      const data = await resp.json();
      if (data.status !== 'authenticated') {
        setError(USER_FACING_ERROR_MESSAGES.unexpected_response);
        return;
      }

      if (data.backup_codes && data.backup_codes.length > 0) {
        setBackupCodesReason('first_time');
        setBackupCodes(data.backup_codes);
        setStep('backup-codes');
      } else {
        onAuthSuccess(trimmed);
      }
    } catch {
      setError(USER_FACING_ERROR_MESSAGES.login_network_error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackupVerify = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!backupCode.trim()) {
      setError(USER_FACING_ERROR_MESSAGES.missing_backup_code);
      return;
    }

    clearErrors();
    setLoading(true);
    try {
      const resp = await fetch(
        `${API_BASE_URL}/api/auth/totp/verify-backup`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: trimmed,
            code: backupCode.trim(),
          }),
        },
      );

      if (resp.status === 429) {
        await applyRateLimitResponse(resp);
        return;
      }
      if (resp.status === 400 || resp.status === 401) {
        setError(USER_FACING_ERROR_MESSAGES.invalid_code);
        return;
      }
      if (resp.status === 403) {
        setError(USER_FACING_ERROR_MESSAGES.not_authorized);
        return;
      }

      const data = await resp.json();
      if (data.status !== 'authenticated') {
        setError(USER_FACING_ERROR_MESSAGES.unexpected_response);
        return;
      }
      // First-login (or ran-out-of-codes) backup-code provisioning: backend
      // returns a fresh batch when the user's stored array is empty. Show
      // the save-codes screen before completing auth.
      if (data.backup_codes && data.backup_codes.length > 0) {
        setBackupCodesReason('consumed_all');
        setBackupCodes(data.backup_codes);
        setStep('backup-codes');
        return;
      }
      onAuthSuccess(trimmed);
    } catch {
      setError(USER_FACING_ERROR_MESSAGES.login_network_error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEnroll = async () => {
    clearErrors();
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/totp/enroll`, {
        method: 'POST',
        credentials: 'include',
      });

      if (resp.status === 401) {
        setError(USER_FACING_ERROR_MESSAGES.sign_in_again);
        return;
      }
      if (resp.status === 403) {
        setError(USER_FACING_ERROR_MESSAGES.not_authorized);
        return;
      }

      const data = await resp.json().catch(() => ({}));

      if (resp.status === 409 || data.status === 'already_enrolled') {
        // Already enrolled — drop straight to authenticated state.
        const trimmed = email.trim().toLowerCase();
        onAuthSuccess(trimmed);
        return;
      }

      if (data.status === 'enrollment_pending' && data.qr_png) {
        setQrPng(data.qr_png);
        setCode('');
        setShowBackupInput(false);
        setBackupCode('');
        setStep('qr-enroll');
        return;
      }

      setError(USER_FACING_ERROR_MESSAGES.unexpected_response);
    } catch {
      setError(USER_FACING_ERROR_MESSAGES.login_network_error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartReset = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError(USER_FACING_ERROR_MESSAGES.missing_email);
      return;
    }

    clearErrors();
    // Clear any previously typed reset code before (re)requesting one — the
    // server overwrites the stored code, so only the newest emailed one is
    // valid. Covers success, 429, and error outcomes.
    setCode('');
    setLoading(true);
    try {
      const resp = await fetch(
        `${API_BASE_URL}/api/auth/totp/reset/send-code`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: trimmed }),
        },
      );

      if (resp.status === 403) {
        setError(USER_FACING_ERROR_MESSAGES.not_authorized);
        return;
      }
      if (resp.status === 429) {
        await applyRateLimitResponse(
          resp,
          USER_FACING_ERROR_MESSAGES.otp_too_many_requests,
        );
        return;
      }
      if (resp.status === 501 || resp.status === 503) {
        setError(USER_FACING_ERROR_MESSAGES.email_service_unavailable);
        return;
      }

      const data = await resp.json().catch(() => ({}));
      if (data.status === 'reset_code_sent') {
        setCode('');
        setStep('reset-email-code');
        setResendCooldown(60);
      } else {
        setError(USER_FACING_ERROR_MESSAGES.unexpected_response);
      }
    } catch {
      setError(USER_FACING_ERROR_MESSAGES.login_network_error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetVerifyCode = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!code || code.length !== 6) {
      setError(USER_FACING_ERROR_MESSAGES.missing_code);
      return;
    }

    clearErrors();
    setLoading(true);
    try {
      const resp = await fetch(
        `${API_BASE_URL}/api/auth/totp/reset/verify-code`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: trimmed, code }),
        },
      );

      if (resp.status === 429) {
        await applyRateLimitResponse(resp);
        return;
      }
      if (resp.status === 400 || resp.status === 401) {
        setError(USER_FACING_ERROR_MESSAGES.invalid_code);
        return;
      }

      const data = await resp.json().catch(() => ({}));
      if (data.status === 'reset_pending' && data.qr_png) {
        setQrPng(data.qr_png);
        setCode('');
        setStep('reset-qr');
      } else {
        setError(USER_FACING_ERROR_MESSAGES.unexpected_response);
      }
    } catch {
      setError(USER_FACING_ERROR_MESSAGES.login_network_error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetFinalize = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!code || code.length !== 6) {
      setError(USER_FACING_ERROR_MESSAGES.missing_code);
      return;
    }

    clearErrors();
    setLoading(true);
    try {
      const resp = await fetch(
        `${API_BASE_URL}/api/auth/totp/reset/finalize`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: trimmed, code }),
        },
      );

      if (resp.status === 429) {
        await applyRateLimitResponse(resp);
        return;
      }
      if (resp.status === 400 || resp.status === 401) {
        setError(USER_FACING_ERROR_MESSAGES.invalid_code);
        return;
      }

      const data = await resp.json().catch(() => ({}));
      if (data.status !== 'authenticated') {
        setError(USER_FACING_ERROR_MESSAGES.unexpected_response);
        return;
      }

      if (data.backup_codes && data.backup_codes.length > 0) {
        setBackupCodes(data.backup_codes);
        setStep('reset-backup-codes');
      } else {
        onAuthSuccess(trimmed);
      }
    } catch {
      setError(USER_FACING_ERROR_MESSAGES.login_network_error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelReset = () => {
    clearErrors();
    setCode('');
    setQrPng(null);
    setStep('totp-code');
  };

  const handleUseEmail = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError(USER_FACING_ERROR_MESSAGES.missing_email);
      return;
    }
    if (!validateEmail(trimmed)) {
      setError(USER_FACING_ERROR_MESSAGES.invalid_email);
      return;
    }

    clearErrors();
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/send-code`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });

      if (resp.status === 403) {
        setError(USER_FACING_ERROR_MESSAGES.not_authorized);
        return;
      }
      if (resp.status === 429) {
        await applyRateLimitResponse(
          resp,
          USER_FACING_ERROR_MESSAGES.otp_too_many_requests,
        );
        return;
      }
      if (resp.status === 501 || resp.status === 503) {
        setError(USER_FACING_ERROR_MESSAGES.email_service_unavailable);
        return;
      }

      const data = await resp.json();

      if (data.status === 'authenticated') {
        // Bypass mode (AUTH_SKIP_CODE on server): backend issued session cookie directly
        onAuthSuccess(trimmed);
        return;
      }

      if (data.status === 'code_sent') {
        setHasBackupCodes(Boolean(data.has_backup_codes));
        setCode('');
        setStep('code');
        setResendCooldown(60);
      } else {
        setError(USER_FACING_ERROR_MESSAGES.unexpected_response);
      }
    } catch {
      setError(USER_FACING_ERROR_MESSAGES.login_network_error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (
    e: React.KeyboardEvent,
    action: () => void,
  ) => {
    if (e.key === 'Enter') action();
  };

  const renderEmailStep = () => (
    <>
      <DialogContent>
        <Box py={1}>
          <Typography variant="body1" gutterBottom>
            {cacAvailable
              ? 'Sign in with your CAC, or use another option below.'
              : 'Please enter your email address to access Waypoint.'}
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            gutterBottom
          >
            Access is restricted to authorized users only.
          </Typography>
          <CacLogin onAvailabilityChange={setCacAvailable} onAuthSuccess={onAuthSuccess} />
          {error && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
              {toUserFacingError(error)}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) =>
              handleKeyPress(e, handleEmailSubmit)
            }
            sx={{ mt: 2 }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', px: 3, pb: 2 }}>
        <Button
          onClick={() => handleEmailSubmit()}
          variant={cacAvailable ? 'outlined' : 'contained'}
          color="primary"
          disabled={!email.trim() || loading}
          fullWidth
        >
          {loading ? <CircularProgress size={24} /> : 'Send 6-digit code to email'}
        </Button>
        <Button
          size="small"
          onClick={handleUseTotp}
          disabled={!email.trim() || loading}
          sx={{ mt: 1, textTransform: 'none' }}
        >
          Use authenticator app instead
        </Button>
        <Button
          size="small"
          color="secondary"
          onClick={() => setSignupOpen(true)}
          disabled={loading}
          sx={{ mt: 1, textTransform: 'none' }}
        >
          Create new account
        </Button>
      </DialogActions>
    </>
  );

  const renderBackupLoginStep = () => (
    <>
      <DialogContent>
        <Box py={1}>
          <Typography variant="body1" gutterBottom>
            Sign in with one of your backup codes.
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            gutterBottom
          >
            We previously provided these codes to you when you first logged in. Enter any and only one backup code to login. Each code can only be used once.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
              {toUserFacingError(error)}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Backup code"
            fullWidth
            variant="outlined"
            value={backupCode}
            onChange={(e) => setBackupCode(e.target.value)}
            onKeyDown={(e) => handleKeyPress(e, handleBackupVerify)}
            inputProps={{ maxLength: 8 }}
            sx={{ mt: 2 }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', px: 3, pb: 2 }}>
        <Button
          onClick={handleBackupVerify}
          variant="contained"
          color="primary"
          disabled={!backupCode.trim() || loading}
          fullWidth
        >
          {loading ? <CircularProgress size={24} /> : 'Verify Backup Code'}
        </Button>
        <Button
          size="small"
          onClick={() => {
            clearErrors();
            setBackupCode('');
            setStep('code');
          }}
          disabled={loading}
          sx={{ mt: 1, textTransform: 'none' }}
        >
          Back
        </Button>
      </DialogActions>
    </>
  );

  const renderCodeStep = () => (
    <>
      <DialogContent>
        <Box py={1}>
          <Typography variant="body1" gutterBottom>
            Check your email for a 6-digit code.
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            We sent a code to {maskEmail(email.trim().toLowerCase())}.
            Check your inbox (and spam folder).
          </Typography>
          {enrollAfterLogin && (
            <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
              You don&apos;t have an authenticator set up yet. We&apos;ll
              email you a 6-digit code first; you can register your
              authenticator after signing in.
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
              {toUserFacingError(error)}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="6-digit code"
            fullWidth
            variant="outlined"
            value={code}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '');
              if (val.length <= 6) setCode(val);
            }}
            onKeyDown={(e) => handleKeyPress(e, handleVerifyCode)}
            inputProps={{ maxLength: 6, inputMode: 'numeric' }}
            sx={{ mt: 2 }}
          />
          <Button
            size="small"
            sx={{ mt: 1 }}
            onClick={handleResend}
            disabled={resendCooldown > 0 || loading}
          >
            {resendCooldown > 0
              ? `Resend code (${resendCooldown}s)`
              : 'Resend code'}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', px: 3, pb: 2 }}>
        <Button
          onClick={handleVerifyCode}
          variant="contained"
          color="primary"
          disabled={code.length !== 6 || loading}
          fullWidth
        >
          {loading ? <CircularProgress size={24} /> : 'Verify'}
        </Button>
        {!enrollAfterLogin && (
          <Button
            size="small"
            onClick={handleUseTotp}
            disabled={loading}
            sx={{ mt: 1, textTransform: 'none' }}
          >
            Use authenticator app instead
          </Button>
        )}
        {!enrollAfterLogin && hasBackupCodes && (
          <Button
            size="small"
            sx={{ mt: 1 }}
            onClick={() => {
              clearErrors();
              setBackupCode('');
              setStep('backup-login');
            }}
            disabled={loading}
          >
            Use backup code
          </Button>
        )}
      </DialogActions>
    </>
  );

  const renderQREnrollStep = () => (
    <>
      <DialogContent>
        <Box py={1}>
          <Typography variant="body1" gutterBottom>
            Scan this code with your authenticator app, then enter
            the 6-digit code it shows.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Free authenticator apps:{' '}
            <strong>Google Authenticator</strong> (
            <a
              href="https://apps.apple.com/app/google-authenticator/id388497605"
              target="_blank"
              rel="noopener noreferrer"
            >
              iOS
            </a>
            {' / '}
            <a
              href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
              target="_blank"
              rel="noopener noreferrer"
            >
              Android
            </a>
            ),{' '}
            <strong>Microsoft Authenticator</strong> (
            <a
              href="https://apps.apple.com/app/microsoft-authenticator/id983156458"
              target="_blank"
              rel="noopener noreferrer"
            >
              iOS
            </a>
            {' / '}
            <a
              href="https://play.google.com/store/apps/details?id=com.azure.authenticator"
              target="_blank"
              rel="noopener noreferrer"
            >
              Android
            </a>
            ), or{' '}
            <strong>Authy</strong> (
            <a
              href="https://apps.apple.com/app/authy/id494168017"
              target="_blank"
              rel="noopener noreferrer"
            >
              iOS
            </a>
            {' / '}
            <a
              href="https://play.google.com/store/apps/details?id=com.authy.authy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Android
            </a>
            )
          </Typography>
          <Box display="flex" justifyContent="center" my={2}>
            {qrPng && (
              <img
                src={`data:image/png;base64,${qrPng}`}
                alt="TOTP QR Code"
                style={{ width: 200, height: 200 }}
              />
            )}
          </Box>
          {error && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
              {toUserFacingError(error)}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="6-digit verification code"
            fullWidth
            variant="outlined"
            value={code}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '');
              if (val.length <= 6) setCode(val);
            }}
            onKeyDown={(e) => handleKeyPress(e, handleTotpVerify)}
            inputProps={{ maxLength: 6, inputMode: 'numeric' }}
            sx={{ mt: 2 }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', px: 3, pb: 2 }}>
        <Button
          onClick={handleTotpVerify}
          variant="contained"
          color="primary"
          disabled={code.length !== 6 || loading}
          fullWidth
        >
          {loading ? <CircularProgress size={24} /> : 'Verify'}
        </Button>
        <Button
          size="small"
          onClick={handleUseEmail}
          disabled={loading}
          sx={{ mt: 1, textTransform: 'none' }}
        >
          Receive code via email instead
        </Button>
      </DialogActions>
    </>
  );

  const renderTOTPCodeStep = () => (
    <>
      <DialogContent>
        <Box py={1}>
          <Typography variant="body1" gutterBottom>
            Enter the 6-digit code from your authenticator app.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
              {toUserFacingError(error)}
            </Alert>
          )}
          {!showBackupInput ? (
            <>
              <TextField
                autoFocus
                margin="dense"
                label="6-digit verification code"
                fullWidth
                variant="outlined"
                value={code}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '');
                  if (val.length <= 6) setCode(val);
                }}
                onKeyDown={(e) => handleKeyPress(e, handleTotpVerify)}
                inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                sx={{ mt: 2 }}
              />
            </>
          ) : (
            <>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ mt: 1 }}
              >
                Enter one of your backup codes:
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                label="Backup code"
                fullWidth
                variant="outlined"
                value={backupCode}
                onChange={(e) => setBackupCode(e.target.value)}
                onKeyDown={(e) => handleKeyPress(e, handleBackupVerify)}
                sx={{ mt: 2 }}
              />
              <Button
                size="small"
                sx={{ mt: 1 }}
                onClick={() => {
                  setShowBackupInput(false);
                  clearErrors();
                }}
              >
                Use authenticator code instead
              </Button>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', px: 3, pb: 2 }}>
        {!showBackupInput ? (
          <Button
            onClick={handleTotpVerify}
            variant="contained"
            color="primary"
            disabled={code.length !== 6 || loading}
            fullWidth
          >
            {loading ? <CircularProgress size={24} /> : 'Verify'}
          </Button>
        ) : (
          <Button
            onClick={handleBackupVerify}
            variant="contained"
            color="primary"
            disabled={!backupCode.trim() || loading}
            fullWidth
          >
            {loading ? <CircularProgress size={24} /> : 'Verify Backup Code'}
          </Button>
        )}
        <Button
          size="small"
          onClick={handleUseEmail}
          disabled={loading}
          sx={{ mt: 1, textTransform: 'none' }}
        >
          Receive code via email instead
        </Button>
        {!showBackupInput && (
          <Button
            size="small"
            sx={{ mt: 1 }}
            onClick={() => {
              setShowBackupInput(true);
              clearErrors();
            }}
          >
            Use backup code
          </Button>
        )}
        <Button
          size="small"
          onClick={handleStartReset}
          disabled={loading}
          sx={{ mt: 1, textTransform: 'none' }}
        >
          Lost your authenticator? Reset it.
        </Button>
      </DialogActions>
    </>
  );

  const renderBackupCodesStep = () => (
    <>
      <DialogContent>
        <Box py={1}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {backupCodesReason === 'consumed_all'
              ? 'You have consumed all of your existing backup codes. Here are new codes. Save them in a safe place.'
              : 'Save these backup codes in a safe place. Each code can only be used once.'}
          </Alert>
          <Box
            sx={{
              bgcolor: 'action.hover',
              p: 2,
              borderRadius: 1,
              fontFamily: 'monospace',
            }}
          >
            {(backupCodes || []).map((bc, i) => (
              <Typography
                key={i}
                variant="body2"
                sx={{ fontFamily: 'monospace' }}
              >
                {bc}
              </Typography>
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            const trimmed = email.trim().toLowerCase();
            onAuthSuccess(trimmed);
          }}
          variant="contained"
          color="primary"
        >
          I've saved my codes
        </Button>
      </DialogActions>
    </>
  );

  const renderResetEmailCodeStep = () => (
    <>
      <DialogContent>
        <Box py={1}>
          <Typography variant="body1" gutterBottom>
            Check your email
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            We sent a 6-digit security code to{' '}
            {maskEmail(email.trim().toLowerCase())}. Enter it below to
            reset your authenticator app. If you didn&apos;t request this,
            just close this window — your current authenticator is still
            active.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
              {toUserFacingError(error)}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="6-digit code"
            fullWidth
            variant="outlined"
            value={code}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '');
              if (val.length <= 6) setCode(val);
            }}
            onKeyDown={(e) => handleKeyPress(e, handleResetVerifyCode)}
            inputProps={{ maxLength: 6, inputMode: 'numeric' }}
            sx={{ mt: 2 }}
          />
          <Button
            size="small"
            sx={{ mt: 1 }}
            onClick={handleStartReset}
            disabled={resendCooldown > 0 || loading}
          >
            {resendCooldown > 0
              ? `Resend code (${resendCooldown}s)`
              : 'Resend code'}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', px: 3, pb: 2 }}>
        <Button
          onClick={handleResetVerifyCode}
          variant="contained"
          color="primary"
          disabled={code.length !== 6 || loading}
          fullWidth
        >
          {loading ? <CircularProgress size={24} /> : 'Verify'}
        </Button>
        <Button
          size="small"
          onClick={handleCancelReset}
          disabled={loading}
          sx={{ mt: 1, textTransform: 'none' }}
        >
          Cancel
        </Button>
      </DialogActions>
    </>
  );

  const renderResetQRStep = () => (
    <>
      <DialogContent>
        <Box py={1}>
          <Typography variant="body1" gutterBottom>
            Scan your new authenticator
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Add this entry to your authenticator app, then enter the
            6-digit code it shows. Your old authenticator will stop
            working after this step.
          </Typography>
          <Box display="flex" justifyContent="center" my={2}>
            {qrPng && (
              <img
                src={`data:image/png;base64,${qrPng}`}
                alt="TOTP QR Code"
                style={{ width: 200, height: 200 }}
              />
            )}
          </Box>
          {error && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
              {toUserFacingError(error)}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="6-digit verification code"
            fullWidth
            variant="outlined"
            value={code}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '');
              if (val.length <= 6) setCode(val);
            }}
            onKeyDown={(e) => handleKeyPress(e, handleResetFinalize)}
            inputProps={{ maxLength: 6, inputMode: 'numeric' }}
            sx={{ mt: 2 }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', px: 3, pb: 2 }}>
        <Button
          onClick={handleResetFinalize}
          variant="contained"
          color="primary"
          disabled={code.length !== 6 || loading}
          fullWidth
        >
          {loading ? <CircularProgress size={24} /> : 'Verify & finalize'}
        </Button>
      </DialogActions>
    </>
  );

  const renderResetBackupCodesStep = () => (
    <>
      <DialogContent>
        <Box py={1}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your old backup codes have been invalidated. Save these new
            ones in a safe place. Each code can only be used once.
          </Alert>
          <Box
            sx={{
              bgcolor: 'action.hover',
              p: 2,
              borderRadius: 1,
              fontFamily: 'monospace',
            }}
          >
            {(backupCodes || []).map((bc, i) => (
              <Typography
                key={i}
                variant="body2"
                sx={{ fontFamily: 'monospace' }}
              >
                {bc}
              </Typography>
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            const trimmed = email.trim().toLowerCase();
            onAuthSuccess(trimmed);
          }}
          variant="contained"
          color="primary"
        >
          I've saved these
        </Button>
      </DialogActions>
    </>
  );

  return (
    <>
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <LockIcon color="primary" />
          <Typography variant="h6">Sign In</Typography>
        </Box>
      </DialogTitle>
      {step === 'email' && renderEmailStep()}
      {step === 'code' && renderCodeStep()}
      {step === 'qr-enroll' && renderQREnrollStep()}
      {step === 'totp-code' && renderTOTPCodeStep()}
      {step === 'backup-login' && renderBackupLoginStep()}
      {step === 'backup-codes' && renderBackupCodesStep()}
      {step === 'reset-email-code' && renderResetEmailCodeStep()}
      {step === 'reset-qr' && renderResetQRStep()}
      {step === 'reset-backup-codes' && renderResetBackupCodesStep()}
    </Dialog>
    <SignupForm
      open={signupOpen}
      onClose={() => setSignupOpen(false)}
      initialEmail={email}
    />
    </>
  );
};

export default EmailGate;
